import { Formiz, useForm } from "@formiz/core";
import { isPattern, isRequired } from "@formiz/validations";
import { useTranslation } from "@next-platform/client-common-core";
import { Spacer, Spinner } from "@nextui-org/react";
import React, { useCallback, useMemo } from "react";

import FormField from "@/components/common/FormField";
import { SpicyButton, SpicyInput, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import ConfirmEmailChangeActions from "@/pages/UpdateEmail/components/ConfirmEmailChangeActions";
import { VERIFICATION_CODE_LENGTH, VERIFICATION_CODE_PATTERN } from "@/pages/UpdateEmail/constants";
import formatCountdown from "@/pages/UpdateEmail/utils/formatCountdown";

type ConfirmEmailChangeFormProps = {
  pendingEmail: string;
  secondsUntilExpiry: number | null;
  secondsUntilResend: number;
  hasExpired: boolean;
  isSubmitting: boolean;
  isResending: boolean;
  errorMessage: string | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onEditEmail: () => void;
  "data-testid"?: string;
};

const ConfirmEmailChangeForm = ({
  pendingEmail,
  secondsUntilExpiry,
  secondsUntilResend,
  hasExpired,
  isSubmitting,
  isResending,
  errorMessage,
  onSubmit,
  onResend,
  onEditEmail,
  "data-testid": testId = "ConfirmEmailChangeForm",
}: ConfirmEmailChangeFormProps) => {
  const { t } = useTranslation();
  const codeForm = useForm();

  const handleValidSubmit = useCallback(
    (values?: object) => {
      const { code } = (values ?? {}) as { code?: string };
      onSubmit(code ?? "");
    },
    [onSubmit],
  );

  const codeValidations = useMemo(
    () => [
      { rule: isRequired(), message: t("user:updateEmail.verify.codeRequired") },
      { rule: isPattern(VERIFICATION_CODE_PATTERN), message: t("user:updateEmail.verify.codeInvalidFormat") },
    ],
    [t],
  );

  const descriptionValues = useMemo(() => ({ email: pendingEmail }), [pendingEmail]);
  const expiryValues = useMemo(() => ({ time: formatCountdown(secondsUntilExpiry ?? 0) }), [secondsUntilExpiry]);

  return (
    <div className="w-full" data-testid={testId}>
      <Text h3>
        <Trans i18nKey="user:updateEmail.verify.heading" />
      </Text>
      <Spacer y={2} />
      <Text variant="md" type="opacity-60" className="block leading-7">
        <Trans i18nKey="user:updateEmail.verify.description" values={descriptionValues} />
      </Text>
      <Spacer y={3} />
      <Formiz connect={codeForm} onValidSubmit={handleValidSubmit}>
        <form className="flex w-full flex-col" noValidate onSubmit={codeForm.submit}>
          <div className="w-full">
            <FormField
              component={SpicyInput}
              variant="primary"
              color="primary"
              title={t("user:updateEmail.verify.codeLabel")}
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={VERIFICATION_CODE_LENGTH}
              spacer={2}
              aria-labelledby="VerificationCode"
              data-testid={`${testId}-CodeInput`}
              validations={codeValidations}
            />
          </div>
          {hasExpired ? (
            <Text type="error" className="mb-6 block">
              <Trans i18nKey="user:updateEmail.verify.expired" />
            </Text>
          ) : null}
          {!hasExpired && secondsUntilExpiry !== null ? (
            <Text variant="sm" type="opacity-60" className="mb-6 block">
              <Trans i18nKey="user:updateEmail.verify.expiresIn" values={expiryValues} />
            </Text>
          ) : null}
          {errorMessage ? (
            <div className="mb-6 text-left" data-testid={`${testId}-Error`}>
              <Text type="error">{errorMessage}</Text>
            </div>
          ) : null}
          <SpicyButton
            variant="primary_large"
            color="primary_strong"
            type="submit"
            className="!w-full"
            aria-labelledby="confirm"
            data-testid={`${testId}-SubmitButton`}
            isDisabled={!codeForm.isValid || isSubmitting || isResending || hasExpired}
          >
            {isSubmitting ? <Spinner color="current" size="sm" /> : <Trans i18nKey="user:updateEmail.verify.submit" />}
          </SpicyButton>
        </form>
      </Formiz>
      <ConfirmEmailChangeActions
        secondsUntilResend={secondsUntilResend}
        isResending={isResending}
        isSubmitting={isSubmitting}
        onResend={onResend}
        onEditEmail={onEditEmail}
      />
    </div>
  );
};

export default React.memo(ConfirmEmailChangeForm);
